
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { HealthLog } from '../types';
import { THAI_LABELS } from '../constants';
import Swal from 'sweetalert2';

const Logs: React.FC = () => {
  const [logs, setLogs] = useState<HealthLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [userHeight, setUserHeight] = useState<string>('');
  
  const initialFormState = {
    date: new Date().toISOString().split('T')[0],
    weight: '', height: '', systolic: '', diastolic: '',
    triglyceride: '', cholesterol: '', hdl: '', ldl: '',
    fbs: '', ast: '', alt: ''
  };
  
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    fetchLogs();
    fetchProfileHeight();
    
    const handleRefresh = () => fetchLogs();
    window.addEventListener('refreshData', handleRefresh);
    return () => window.removeEventListener('refreshData', handleRefresh);
  }, []);

  const fetchProfileHeight = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase.from('profiles').select('height').eq('id', user.id).single();
      if (data?.height) {
        const heightStr = data.height.toString();
        setUserHeight(heightStr);
        if (!editingId) setFormData(prev => ({ ...prev, height: heightStr }));
      }
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase
        .from('health_logs')
        .select('*')
        .order('date', { ascending: false });
      if (data) setLogs(data);
    }
    setLoading(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const startEdit = (log: HealthLog) => {
    setEditingId(log.id);
    setFormData({
      date: log.date,
      weight: log.weight.toString(),
      height: log.height.toString(),
      systolic: log.systolic.toString(),
      diastolic: log.diastolic.toString(),
      triglyceride: log.triglyceride.toString(),
      cholesterol: log.cholesterol.toString(),
      hdl: log.hdl?.toString() || '',
      ldl: log.ldl?.toString() || '',
      fbs: log.fbs?.toString() || '',
      ast: log.ast?.toString() || '',
      alt: log.alt?.toString() || ''
    });
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบข้อมูล?',
      text: "คุณจะไม่สามารถกู้คืนข้อมูลบันทึกนี้ได้",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'ใช่, ลบเลย',
      cancelButtonText: 'ยกเลิก',
      background: document.documentElement.classList.contains('dark') ? '#1e293b' : '#fff',
      color: document.documentElement.classList.contains('dark') ? '#f1f5f9' : '#1e293b'
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const { error } = await supabase.from('health_logs').delete().eq('id', id);
        if (error) throw error;
        
        Swal.fire({ title: 'ลบสำเร็จ', icon: 'success', timer: 1500, showConfirmButton: false });
        fetchLogs();
      } catch (err: any) {
        Swal.fire({ title: 'เกิดข้อผิดพลาด', text: err.message, icon: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { date, weight, height, systolic, diastolic, triglyceride, cholesterol } = formData;
    if (!date || !weight || !height || !systolic || !diastolic || !triglyceride || !cholesterol) {
       Swal.fire({ title: 'ข้อมูลไม่ครบถ้วน', text: 'กรุณากรอกข้อมูลที่มีเครื่องหมาย * ให้ครบ', icon: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ไม่พบข้อมูลผู้ใช้');

      const w = parseFloat(weight);
      const h = parseFloat(height);
      const bmi = w / Math.pow(h / 100, 2);

      const logData = {
        user_id: user.id,
        date: formData.date,
        weight: w,
        height: h,
        bmi: bmi,
        systolic: parseInt(systolic),
        diastolic: parseInt(diastolic),
        triglyceride: parseInt(triglyceride),
        cholesterol: parseInt(cholesterol),
        hdl: formData.hdl ? parseFloat(formData.hdl) : 0,
        ldl: formData.ldl ? parseFloat(formData.ldl) : 0,
        fbs: formData.fbs ? parseFloat(formData.fbs) : null,
        ast: formData.ast ? parseFloat(formData.ast) : null,
        alt: formData.alt ? parseFloat(formData.alt) : null,
      };

      if (editingId) {
        const { error } = await supabase.from('health_logs').update(logData).eq('id', editingId);
        if (error) throw error;
        Swal.fire({ title: 'อัปเดตสำเร็จ', icon: 'success', timer: 1500, showConfirmButton: false });
      } else {
        const { error } = await supabase.from('health_logs').insert([logData]);
        if (error) throw error;
        Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1500, showConfirmButton: false });
      }

      setShowAddForm(false);
      setEditingId(null);
      setFormData({ ...initialFormState, height: userHeight });
      fetchLogs();
      window.dispatchEvent(new CustomEvent('refreshData'));
    } catch (err: any) {
      Swal.fire({ title: 'เกิดข้อผิดพลาด', text: err.message, icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isAnomaly = (log: HealthLog) => {
    return log.systolic >= 140 || log.diastolic >= 90 || log.ldl >= 160 || (log.fbs && log.fbs >= 126);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">สมุดบันทึกดิจิทัล</h2>
        <button 
          onClick={() => {
            if (showAddForm) {
              setEditingId(null);
              setFormData({ ...initialFormState, height: userHeight });
            }
            setShowAddForm(!showAddForm);
          }}
          className={`px-4 py-2 text-white text-sm font-bold rounded-xl shadow-lg transition-all active:scale-95 ${showAddForm ? 'bg-slate-400 dark:bg-slate-700' : 'bg-blue-600 dark:bg-blue-500 shadow-blue-100 dark:shadow-none hover:scale-105'}`}
        >
          {showAddForm ? 'ยกเลิก' : '+ บันทึกค่าสุขภาพ'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border-2 border-blue-50 dark:border-slate-800 shadow-xl space-y-6 animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-2 mb-2">
             <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
             <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight">
               {editingId ? 'แก้ไขข้อมูลบันทึก' : 'เพิ่มบันทึกสุขภาพใหม่'}
             </h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">วันที่ตรวจ *</label>
              <input type="date" name="date" required value={formData.date} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-blue-500" />
            </div>
            
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">น้ำหนัก (กก.) *</label>
              <input type="number" step="0.1" name="weight" required value={formData.weight} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-blue-500" placeholder="0.0" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ส่วนสูง (ซม.) *</label>
              <input type="number" step="0.1" name="height" required value={formData.height} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-blue-500" placeholder="170" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">SYS (บน) *</label>
              <input type="number" name="systolic" required value={formData.systolic} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-red-500" placeholder="120" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">DIA (ล่าง) *</label>
              <input type="number" name="diastolic" required value={formData.diastolic} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-red-500" placeholder="80" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ไตรกลีเซอไรด์ *</label>
              <input type="number" name="triglyceride" required value={formData.triglyceride} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-amber-500" placeholder="150" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">คอเลสเตอรอล *</label>
              <input type="number" name="cholesterol" required value={formData.cholesterol} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-amber-500" placeholder="200" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">HDL (ไขมันดี)</label>
              <input type="number" step="0.01" name="hdl" value={formData.hdl} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none" placeholder="50.00" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">LDL (ไขมันเลว)</label>
              <input type="number" step="0.01" name="ldl" value={formData.ldl} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none" placeholder="130.00" />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">FBS (น้ำตาล)</label>
              <input type="number" step="0.01" name="fbs" value={formData.fbs} onChange={handleInputChange} className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 outline-none" placeholder="100.00" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">AST/ALT (ตับ)</label>
              <div className="flex gap-2">
                <input type="number" step="0.01" name="ast" value={formData.ast} onChange={handleInputChange} className="w-1/2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs text-slate-700 dark:text-slate-200 outline-none" placeholder="AST" />
                <input type="number" step="0.01" name="alt" value={formData.alt} onChange={handleInputChange} className="w-1/2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs text-slate-700 dark:text-slate-200 outline-none" placeholder="ALT" />
              </div>
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full py-4 bg-blue-600 dark:bg-blue-500 text-white font-bold rounded-2xl shadow-lg shadow-blue-100 dark:shadow-none hover:bg-blue-700 active:scale-95 transition-all">
            {loading ? 'กำลังประมวลผล...' : editingId ? 'อัปเดตข้อมูลบันทึก' : 'คำนวณ BMI และบันทึกข้อมูล'}
          </button>
        </form>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-[32px] shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden transition-colors">
        <div className="p-5 border-b border-slate-50 dark:border-slate-800 flex justify-between items-center">
          <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm">ประวัติการตรวจสุขภาพย้อนหลัง</h3>
        </div>
        <div className="overflow-x-auto">
          {logs.length > 0 ? (
            <table className="w-full text-left border-collapse min-w-[500px]">
              <thead className="bg-slate-50 dark:bg-slate-800 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest transition-colors">
                <tr>
                  <th className="px-5 py-3">วันที่</th>
                  <th className="px-5 py-3 text-center">BMI</th>
                  <th className="px-5 py-3 text-right">ข้อมูลหลัก (BP/Lipid)</th>
                  <th className="px-5 py-3 text-center w-24">จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                {logs.map((log) => (
                  <tr key={log.id} className={`${isAnomaly(log) ? 'bg-red-50/50 dark:bg-red-900/10' : ''} hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors`}>
                    <td className="px-5 py-4">
                        <p className="text-xs font-black text-slate-700 dark:text-slate-300">{new Date(log.date).toLocaleDateString('th-TH')}</p>
                        <p className="text-[10px] text-slate-400">{log.weight}kg / {log.height}cm</p>
                    </td>
                    <td className="px-5 py-4 text-center">
                        <span className={`text-xs font-black ${log.bmi >= 25 ? 'text-amber-500' : 'text-blue-500'}`}>{log.bmi?.toFixed(1)}</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                        <p className="text-xs font-black text-slate-700 dark:text-slate-300">{log.systolic}/{log.diastolic} mmHg</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">TG: {log.triglyceride} | CHOL: {log.cholesterol}</p>
                    </td>
                    <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-2">
                           <button 
                             onClick={() => startEdit(log)}
                             className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                             title="แก้ไข"
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                           </button>
                           <button 
                             onClick={() => handleDelete(log.id)}
                             className="p-2 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                             title="ลบ"
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                           </button>
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-10 text-center text-slate-400 dark:text-slate-600">ยังไม่พบข้อมูลบันทึก</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Logs;
